/* CompareDialog — side-by-side spec comparison for up to 4 UniBuy™ products.
   Pulls the full product payload from /api/infoshop/products-by-pn/batch and
   renders an attribute-by-attribute matrix. Identical values per row are
   surfaced with a subtle "match" badge so the buyer can spot like-for-like
   alternates at a glance. */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { X, CircleCheck, Loader2, ShoppingCart, ExternalLink } from "lucide-react";
import ProductVisual from "./ProductVisual";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROWS = [
  { key: "infoshop_pn",                        label: "UniBuy™ PN",                mono: true },
  { key: "mfg_pn",                             label: "Mfg Part Number",            mono: true },
  { key: "brand",                              label: "Brand / Manufacturer" },
  { key: "category",                           label: "Category" },
  { key: "unspsc",                             label: "UNSPSC",                     mono: true },
  { key: "uom",                                label: "Unit of Measure",            mono: true },
  { key: "dimensions",                         label: "Dimensions" },
  { key: "lead_time_days",                     label: "Lead time (days)",           mono: true },
  { key: "list_price",                         label: "List price (OEM)",           kind: "money" },
  { key: "effective_customer_discount_pct",    label: "Customer discount vs list",  kind: "pct" },
  { key: "selling_price",                      label: "UniBuy™ selling price",     kind: "money", emphasis: true },
];

function fmtCell(val, kind, currency) {
  if (val == null || val === "") return "—";
  if (kind === "money") {
    return `${currency || ""} ${Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`.trim();
  }
  if (kind === "pct") return `${val}%`;
  return String(val);
}

export default function CompareDialog({ open, onOpenChange, pns, onAdd, onRemovePn, onClearAll, onOpenSpec }) {
  const { t } = useLocale();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pns?.length) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/infoshop/products-by-pn/batch`, {
          params: { pns: pns.join(",") },
        });
        if (!cancelled) setProducts(data.items || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, pns]);

  // Per-row "all-match" detector → green dot in the label column.
  const rowMatch = (key) => {
    if (products.length < 2) return false;
    const v0 = products[0]?.[key];
    return products.every((p) => p?.[key] != null && p?.[key] === v0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl max-h-[92vh] overflow-y-auto"
        data-testid="compare-dialog"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-display text-2xl tracking-tight">
                {t("compare.title")}
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs text-slate-600">
                {t("compare.subtitle")}
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              onClick={onClearAll}
              data-testid="compare-clear-all"
              className="text-xs text-slate-500 hover:text-rose-600"
            >
              {t("compare.clear_all")}
            </Button>
          </div>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center text-sm text-slate-500 inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="animate-spin" size={16} /> Loading products…
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-500" data-testid="compare-empty">
            {t("compare.empty")}
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="mt-2 overflow-x-auto">
            {/* Header strip with product cards */}
            <div
              className="grid gap-4 mb-4"
              style={{ gridTemplateColumns: `220px repeat(${products.length}, minmax(220px, 1fr))` }}
              data-testid="compare-header-strip"
            >
              <div /> {/* spacer above the row-label column */}
              {products.map((p) => (
                <div
                  key={p.infoshop_pn}
                  data-testid={`compare-card-${p.infoshop_pn}`}
                  className="relative rounded-xl border border-slate-200 bg-white overflow-hidden"
                >
                  <button
                    onClick={() => onRemovePn(p.infoshop_pn)}
                    data-testid={`compare-remove-${p.infoshop_pn}`}
                    className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-white/95 border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-300 inline-flex items-center justify-center shadow-sm"
                    aria-label="Remove from compare"
                  >
                    <X size={14} />
                  </button>
                  <div className="relative aspect-[4/3] border-b border-slate-100">
                    <ProductVisual product={p} className="absolute inset-0 w-full h-full" />
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{p.brand}</div>
                    <div className="font-display font-semibold text-slate-900 text-sm leading-tight mt-0.5 line-clamp-3">
                      {p.description}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono text-[11px] text-slate-600">{p.infoshop_pn}</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (onOpenSpec) onOpenSpec(p.infoshop_pn);
                        }}
                        data-testid={`compare-spec-link-${p.infoshop_pn}`}
                        className="text-[11px] text-[#0072CE] hover:underline inline-flex items-center gap-0.5"
                      >
                        Spec <ExternalLink size={10} />
                      </button>
                    </div>
                    {onAdd && p.selling_price != null && (
                      <Button
                        onClick={() => onAdd(p, 1)}
                        data-testid={`compare-add-${p.infoshop_pn}`}
                        size="sm"
                        className="mt-3 w-full bg-[#0072CE] hover:bg-[#005BA6] text-white text-xs h-8"
                      >
                        <ShoppingCart size={12} className="mr-1.5" /> Add to cart
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Spec matrix */}
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
              {ROWS.map((r, i) => {
                const match = rowMatch(r.key);
                return (
                  <div
                    key={r.key}
                    className="grid gap-4 px-4 py-3 items-center border-b border-slate-100 last:border-b-0 odd:bg-slate-50/40"
                    style={{ gridTemplateColumns: `220px repeat(${products.length}, minmax(220px, 1fr))` }}
                    data-testid={`compare-row-${r.key}`}
                  >
                    <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 inline-flex items-center gap-1.5">
                      {match && (
                        <CircleCheck
                          size={12}
                          className="text-emerald-600 shrink-0"
                          aria-label="All match"
                        />
                      )}
                      <span>{r.label}</span>
                    </div>
                    {products.map((p) => (
                      <div
                        key={`${r.key}-${p.infoshop_pn}`}
                        className={`text-sm ${r.mono ? "font-mono" : ""} ${
                          r.emphasis ? "font-display font-semibold text-slate-900" : "text-slate-700"
                        } ${match ? "" : ""}`}
                        data-testid={`compare-cell-${r.key}-${p.infoshop_pn}`}
                      >
                        {fmtCell(p[r.key], r.kind, p.currency)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
